import { useEffect, useState } from "react";
import { Plus, Trash2, Calculator, List } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const sumItems = (items) =>
  (items || []).reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0);

export const LineItems = ({ label, items = [], onChange, testid, category = "material", readOnly = false }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [priceList, setPriceList] = useState([]);

  useEffect(() => {
    api.get("/price-items").then(({ data }) => setPriceList(data)).catch(() => {});
  }, []);

  const rowSum = (Number(qty) || 0) * (Number(price) || 0);
  const options = priceList.filter((p) => p.category === category);

  const pickFromList = (id) => {
    const p = priceList.find((x) => x.id === id);
    if (!p) return;
    setName(p.name);
    setPrice(String(p.price));
  };

  const add = () => {
    if (!name.trim()) return;
    onChange([
      ...(items || []),
      { id: `${Date.now()}`, name: name.trim(), qty: Number(qty) || 0, price: Number(price) || 0 },
    ]);
    setName("");
    setQty("1");
    setPrice("");
  };

  const remove = (id) => onChange((items || []).filter((i) => i.id !== id));

  return (
    <div data-testid={testid} className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
        <span className="text-sm font-medium text-slate-700" data-testid={`${testid}-total`}>
          {sumItems(items).toFixed(2)} kr
        </span>
      </div>

      <div className="mt-2 space-y-1">
        {(items || []).map((i) => (
          <div key={i.id} data-testid={`${testid}-row-${i.id}`} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate text-slate-800">{i.name}</span>
            <span className="text-slate-500">
              {i.qty} × {Number(i.price).toFixed(2)} =
            </span>
            <span className="w-20 text-right font-medium text-slate-900">
              {((Number(i.qty) || 0) * (Number(i.price) || 0)).toFixed(2)}
            </span>
            <button
              data-testid={`${testid}-remove-${i.id}`}
              onClick={() => remove(i.id)}
              className={`rounded p-1 text-red-600 transition-colors duration-200 hover:bg-red-50 ${readOnly ? "hidden" : ""}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {options.length > 0 && !readOnly && (
        <div className="mt-2">
          <Select value="" onValueChange={pickFromList}>
            <SelectTrigger data-testid={`${testid}-pricelist-select`} className="h-10">
              <SelectValue placeholder={t("items.pickFromList")} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {options.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`${testid}-pricelist-option-${p.id}`}>
                  {p.name} – {p.price.toFixed(2)} kr/{p.unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!readOnly && (
      <div className="mt-2 grid grid-cols-12 gap-2">
        <Input
          data-testid={`${testid}-name-input`}
          placeholder={t("common.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="col-span-5 h-10"
        />
        <Input
          data-testid={`${testid}-qty-input`}
          type="number"
          step="0.5"
          placeholder={t("common.qty")}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="col-span-3 h-10"
        />
        <Input
          data-testid={`${testid}-price-input`}
          type="number"
          step="0.01"
          placeholder={t("common.price")}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="col-span-3 h-10"
        />
        <Button
          data-testid={`${testid}-add`}
          onClick={add}
          className="col-span-1 h-10 bg-blue-600 px-0 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      )}

      {!readOnly && (
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500" data-testid={`${testid}-calc`}>
        <Calculator className="h-3.5 w-3.5" /> {qty || 0} × {price || 0} = <b>{rowSum.toFixed(2)} kr</b>
        <span className="ml-auto flex items-center gap-1 text-slate-400">
          <List className="h-3 w-3" /> {options.length} {t("items.inPriceList")}
        </span>
      </p>
      )}
    </div>
  );
};

export default LineItems;
